import { Link, useParams, useLocation } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';

import {
  getBlogPostBySlug,
  getBlogPostCommentsBySlug,
} from '../../../api/services/blogPostsApi';

import { createComment } from '../../../api/services/blogPostCommentsApi';

import BlogLayout from '../../../components/main/blog/BlogLayout/BlogLayout';
import BlogSideBar from '../../../components/main/blog/BlogSideBar/BlogSideBar';
import BlogPostMetaTitle from '../../../components/main/blog/BlogPostMetaTitle/BlogPostMetaTitle';
import MainCardImage from '../../../components/main/cards/MainCardImage/MainCardImage';
import LinkifiedText from '../../../components/shared/LinkifiedText/LinkifiedText';
import BlogPostCommentCard from '../../../components/main/cards/BlogPostCommentCard/BlogPostCommentCard';
import BlogPostCommentForm from '../../../components/main/forms/BlogPostCommentForm/BlogPostCommentForm';

import { MdNavigateBefore, MdNavigateNext } from 'react-icons/md';

import './BlogPostDetail.css';

const BlogPostDetail = () => {
  const { slug } = useParams();
  const location = useLocation();

  const [blogPost, setBlogPost] = useState(null);
  const [isLoadingBlogPost, setIsLoadingBlogPost] = useState(true);

  const [comments, setComments] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingComments, setIsLoadingComments] = useState(false);

  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [commentSuccess, setCommentSuccess] = useState('');
  const [commentError, setCommentError] = useState('');

  /* ---------------- Fetch blog post ---------------- */

  const fetchBlogPost = useCallback(async () => {
    setIsLoadingBlogPost(true);

    try {
      const post = await getBlogPostBySlug(slug);
      setBlogPost(post);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingBlogPost(false);
    }
  }, [slug]);

  /* ---------------- Fetch comments ---------------- */

  const fetchComments = useCallback(
    async (pageToLoad = 1) => {
      try {
        setIsLoadingComments(true);

        const { data, totalPages } = await getBlogPostCommentsBySlug(
          slug,
          pageToLoad,
          10,
        );

        setComments(data);
        setTotalPages(totalPages);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoadingComments(false);
      }
    },
    [slug],
  );

  /* ---------------- Pagination ---------------- */

  const handlePrevPage = () => {
    if (page > 1) setPage((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage((prev) => prev + 1);
  };

  /* ---------------- Effects ---------------- */

  useEffect(() => {
    fetchBlogPost();
    setPage(1);
  }, [fetchBlogPost]);

  useEffect(() => {
    fetchComments(page);
  }, [fetchComments, page]);

  useEffect(() => {
    if (!blogPost) return;

    if (location.hash === '#blog-post-detail-comments-title') {
      const el = document.getElementById('blog-post-detail-comments-title');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [blogPost, location.hash]);

  /* ---------------- Add comment ---------------- */

  const handleAddComment = async (commentData) => {
    try {
      setIsSubmittingComment(true);
      setCommentError('');

      await createComment(blogPost._id, commentData);

      // Refresh data
      await fetchBlogPost();
      await fetchComments(1);
      setPage(1);
    } catch (err) {
      console.error(err);

      setCommentError(err.message || 'Error adding comment. Please try again.');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  /* ---------------- UI States ---------------- */

  if (isLoadingBlogPost) return <p>Loading...</p>;

  if (!isLoadingBlogPost && !blogPost) return <p>Blog post not found.</p>;

  return (
    <div className='blog-post-detail-page-container'>
      <BlogLayout sidebar={<BlogSideBar />}>
        <div className='blog-post-detail-card-container'>
          <BlogPostMetaTitle
            blogPost={blogPost}
            commentsCountIconLinkTo={`#blog-post-detail-comments-title`}
          />

          {blogPost.imageUrl && (
            <MainCardImage size='large' img={blogPost.imageUrl} />
          )}

          <p>
            <LinkifiedText>{blogPost.content}</LinkifiedText>
          </p>

          {/* -------- Prev / Next -------- */}
          {(blogPost.prevPost || blogPost.nextPost) && (
            <div className='blog-post-detail-navigation-links-container'>
              <div className='blog-post-detail-navigation-prev-link-container'>
                {blogPost.prevPost && (
                  <Link to={`/blog/${blogPost.prevPost.slug}`}>
                    ← Previous post: {blogPost.prevPost.title}
                  </Link>
                )}
              </div>

              <div className='blog-post-detail-navigation-next-link-container'>
                {blogPost.nextPost && (
                  <Link to={`/blog/${blogPost.nextPost.slug}`}>
                    Next post: {blogPost.nextPost.title} →
                  </Link>
                )}
              </div>
            </div>
          )}

          <hr className='blog-post-detail-comments-section-line' />

          {/* -------- Comments -------- */}
          {!blogPost.disableCommentsOnBlog ? (
            <div className='blog-post-detail-card-comments-section-container'>
              <h4 id='blog-post-detail-comments-title'>Leave a comment</h4>

              {/* Success message */}
              {commentSuccess && (
                <p className='blog-post-comment-success'>{commentSuccess}</p>
              )}

              {/* Error */}
              {commentError && (
                <p className='blog-post-comment-error'>{commentError}</p>
              )}

              <BlogPostCommentForm
                onSubmit={handleAddComment}
                isSubmitting={isSubmittingComment}
                successMsg={commentSuccess}
                setSuccessMsg={setCommentSuccess}
              />

              {isLoadingComments ? (
                <p>Loading comments...</p>
              ) : (
                <div className='blog-post-detail-card-comments-border-container'>
                  <div className='blog-post-detail-card-comments-container'>
                    {comments.length ? (
                      <>
                        {comments.map((comment) => (
                          <BlogPostCommentCard
                            key={comment._id}
                            comment={comment}
                          />
                        ))}

                        {/* -------- Pagination -------- */}
                        {totalPages > 1 && (
                          <div className='blog-post-detail-comments-pagination-buttons-container'>
                            <button
                              className='blog-post-detail-comments-pagination-button'
                              onClick={handlePrevPage}
                              disabled={page === 1}
                            >
                              <span>
                                <MdNavigateBefore className='blog-post-detail-comments-navigation-icon' />
                                Previous page
                              </span>
                            </button>

                            <span className='blog-post-detail-comments-pagination-page-info'>
                              Page {page} of {totalPages}
                            </span>

                            <button
                              className='blog-post-detail-comments-pagination-button'
                              onClick={handleNextPage}
                              disabled={page === totalPages}
                            >
                              <span>
                                Next page
                                <MdNavigateNext className='blog-post-detail-comments-navigation-icon' />
                              </span>
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <p>No Comments Yet.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p>Comments are closed.</p>
          )}
        </div>
      </BlogLayout>
    </div>
  );
};

export default BlogPostDetail;
